(() => {
  'use strict';

  const MODULE_KEY = '__totokaGiftingRitual';
  const ROOT_SELECTOR = '[data-tgr-root]';

  if (window[MODULE_KEY]) {
    window[MODULE_KEY].mount(document);
    return;
  }

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  class TotokaGiftingRitual {
    constructor(root) {
      this.root = root;
      this.journey = root.querySelector('[data-tgr-journey]');
      this.steps = [...root.querySelectorAll('[data-tgr-step]')];
      this.reveals = [...root.querySelectorAll('[data-tgr-reveal]')];
      this.packagingVisual = root.querySelector('[data-tgr-packaging-visual]');
      this.intensity = root.dataset.animationIntensity || 'standard';
      this.observer = null;
      this.frame = null;
      this.destroyed = false;

      this.onViewportChange = this.onViewportChange.bind(this);
      this.init();
    }

    init() {
      if (reducedMotion.matches) {
        this.showAll();
        this.root.style.setProperty('--tgr-path-progress', '1');
        return;
      }

      this.root.classList.add('is-motion-ready');

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (records) => {
            records.forEach((record) => {
              if (!record.isIntersecting) return;
              this.reveal(record.target);
              this.observer?.unobserve(record.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
        );

        this.reveals.forEach((element) => this.observer.observe(element));
      } else {
        this.showAll();
      }

      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.onViewportChange();
    }

    reveal(element) {
      element?.classList.add('is-visible');
    }

    showAll() {
      this.reveals.forEach((element) => this.reveal(element));
    }

    onViewportChange() {
      if (this.frame || this.destroyed) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.updateJourney();
        this.updateCurrentStep();
        this.updatePackagingParallax();
      });
    }

    updateJourney() {
      if (!this.journey) return;

      const rect = this.journey.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const startPoint = viewportHeight * 0.72;
      const travel = Math.max(rect.height * 0.9, 1);
      const progress = clamp((startPoint - rect.top) / travel);

      this.root.style.setProperty('--tgr-path-progress', progress.toFixed(4));
    }

    updateCurrentStep() {
      if (!this.steps.length) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const focusPoint = viewportHeight * 0.52;
      let currentStep = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.steps.forEach((step) => {
        const rect = step.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = Math.abs(center - focusPoint);
        const isNearViewport = rect.bottom > viewportHeight * 0.14 && rect.top < viewportHeight * 0.86;

        if (isNearViewport && distance < closestDistance) {
          currentStep = step;
          closestDistance = distance;
        }
      });

      this.steps.forEach((step) => step.classList.toggle('is-current', step === currentStep));
    }

    updatePackagingParallax() {
      if (!this.packagingVisual) return;

      if (window.innerWidth < 750) {
        this.packagingVisual.style.setProperty('--tgr-packaging-parallax', '0px');
        return;
      }

      const rect = this.packagingVisual.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (rect.bottom < 0 || rect.top > viewportHeight) return;

      const amplitudes = { subtle: 7, standard: 11, strong: 16 };
      const amplitude = amplitudes[this.intensity] || amplitudes.standard;
      const center = rect.top + rect.height / 2;
      const normalized = clamp((center - viewportHeight / 2) / viewportHeight, -1, 1);

      this.packagingVisual.style.setProperty(
        '--tgr-packaging-parallax',
        `${(-normalized * amplitude).toFixed(2)}px`
      );
    }

    revealBlock(target) {
      const block = target?.matches?.('[data-block-id]')
        ? target
        : target?.closest?.('[data-block-id]');

      if (!block) return;

      this.reveal(block);

      if (block.matches('[data-tgr-step]')) {
        this.steps.forEach((step) => step.classList.toggle('is-current', step === block));
      }

      block.scrollIntoView?.({
        block: 'center',
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    destroy() {
      this.destroyed = true;
      this.observer?.disconnect();
      this.observer = null;

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);

      this.root.classList.remove('is-motion-ready');
      this.root.style.removeProperty('--tgr-path-progress');
      this.reveals.forEach((element) => element.classList.remove('is-visible'));
      this.steps.forEach((step) => step.classList.remove('is-current'));
      this.packagingVisual?.style.removeProperty('--tgr-packaging-parallax');
    }
  }

  const rootsWithin = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) return [scope];
    return [...(scope.querySelectorAll?.(ROOT_SELECTOR) || [])];
  };

  const mount = (scope = document) => {
    rootsWithin(scope).forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new TotokaGiftingRitual(root));
    });
  };

  const unmount = (scope) => {
    rootsWithin(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  const start = () => mount(document);

  window[MODULE_KEY] = { mount, unmount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    const selectedBlock = blockId
      ? [...document.querySelectorAll(`${ROOT_SELECTOR} [data-block-id]`)]
        .find((element) => element.dataset.blockId === blockId)
      : event.target.closest?.('[data-block-id]');
    const root = selectedBlock?.closest?.(ROOT_SELECTOR) || event.target.closest?.(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;

    instance?.revealBlock(selectedBlock || event.target);
  });
})();
